import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Icon from '@/components/Icon';
import {
  mdiClose,
  mdiDownload,
  mdiFitToScreenOutline,
  mdiMagnifyMinusOutline,
  mdiMagnifyPlusOutline,
  mdiOpenInNew,
  mdiRotateLeft,
  mdiRotateRight,
} from '@mdi/js';
import styles from './VisualizadorImagem.module.css';

type VisualizadorImagemProps = {
  show: boolean;
  src: string;
  alt?: string;
  /** Nome sugerido para o download. */
  nomeArquivo?: string;
  onClose: () => void;
};

const ESCALA_MIN = 1;
const ESCALA_MAX = 8;
const PASSO_RODA = 1.15;
const PASSO_BOTAO = 1.4;

const clamp = (v: number, min: number, max: number) =>
  Math.min(max, Math.max(min, v));

/**
 * Visualizador de imagem em tela cheia (lightbox), com zoom e pan.
 *
 * A imagem é renderizada em "fit" (escala 1 = cabe na viewport) e ampliada via
 * `transform: translate(...) scale(...)`. Como o translate vem antes do scale,
 * o offset é sempre em pixels de tela, o que simplifica o zoom sob o cursor.
 *
 * Zoom por roda do mouse/botões, pan por arraste (só quando ampliada),
 * duplo clique alterna fit ↔ 2x, rotação em passos de 90°, e Esc/+/-/0/r/R
 * no teclado. A rotação é só visual — o download continua sendo o original.
 * Pinch de dois dedos não é suportado — o pan de um dedo funciona.
 */
const VisualizadorImagem = ({
  show,
  src,
  alt,
  nomeArquivo,
  onClose,
}: VisualizadorImagemProps) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const [escala, setEscala] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  /** Graus acumulados (não normalizados, para a transição girar pelo lado certo). */
  const [rotacao, setRotacao] = useState(0);
  const deitada = Math.abs(rotacao / 90) % 2 === 1;

  // Refs de arraste: mutáveis para não re-renderizar a cada pointermove.
  const arraste = useRef<{
    /** Offset de origem: `clientX - offset.x` no pointerdown. */
    x: number;
    y: number;
    downX: number;
    downY: number;
    moveu: boolean;
    /** Se o gesto começou no fundo (não na imagem nem na barra). */
    noFundo: boolean;
  } | null>(null);
  const [arrastando, setArrastando] = useState(false);

  /**
   * Fator extra para que a imagem girada 90°/270° continue cabendo na
   * viewport: o CSS ajusta a caixa sem rotação, e girada largura e altura
   * trocam de lugar.
   */
  const fatorRotacao = useCallback((estaDeitada: boolean) => {
    const img = imgRef.current;
    const overlay = overlayRef.current;
    if (!estaDeitada || !img || !overlay) return 1;

    return Math.min(
      1,
      overlay.clientWidth / img.offsetHeight,
      overlay.clientHeight / img.offsetWidth,
    );
  }, []);

  /**
   * Impede que a imagem seja arrastada para fora da viewport: o offset é
   * limitado à metade do que "sobra" da imagem ampliada em cada eixo.
   */
  const limitarOffset = useCallback(
    (x: number, y: number, escalaAtual: number, estaDeitada = deitada) => {
      const img = imgRef.current;
      const overlay = overlayRef.current;
      if (!img || !overlay) return { x, y };

      const escalaReal = escalaAtual * fatorRotacao(estaDeitada);
      const largura = estaDeitada ? img.offsetHeight : img.offsetWidth;
      const altura = estaDeitada ? img.offsetWidth : img.offsetHeight;

      const maxX = Math.max(0, (largura * escalaReal - overlay.clientWidth) / 2);
      const maxY = Math.max(0, (altura * escalaReal - overlay.clientHeight) / 2);

      return { x: clamp(x, -maxX, maxX), y: clamp(y, -maxY, maxY) };
    },
    [deitada, fatorRotacao],
  );

  const girar = useCallback(
    (graus: 90 | -90) => {
      setRotacao((r) => r + graus);
      // A caixa visual troca de orientação: re-limita o pan com ela.
      setOffset((off) => limitarOffset(off.x, off.y, escala, !deitada));
    },
    [limitarOffset, escala, deitada],
  );

  const resetar = useCallback(() => {
    setEscala(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  /**
   * Aplica `fator` de zoom mantendo fixo o ponto (px, py) da tela — quando
   * omitido, usa o centro da viewport (caso dos botões e do teclado).
   */
  const aplicarZoom = useCallback(
    (fator: number, px?: number, py?: number) => {
      const overlay = overlayRef.current;
      if (!overlay) return;

      setEscala((escalaAtual) => {
        const nova = clamp(escalaAtual * fator, ESCALA_MIN, ESCALA_MAX);
        if (nova === escalaAtual) return escalaAtual;

        if (nova === ESCALA_MIN) {
          setOffset({ x: 0, y: 0 });
          return nova;
        }

        const rect = overlay.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const alvoX = px ?? cx;
        const alvoY = py ?? cy;
        const razao = nova / escalaAtual;

        setOffset((off) =>
          limitarOffset(
            alvoX - cx - razao * (alvoX - cx - off.x),
            alvoY - cy - razao * (alvoY - cy - off.y),
            nova,
          ),
        );

        return nova;
      });
    },
    [limitarOffset],
  );

  // Zera o estado ao abrir e ao trocar de imagem.
  useEffect(() => {
    if (!show) return;
    resetar();
    setRotacao(0);
  }, [show, src, resetar]);

  useEffect(() => {
    if (!show) return;

    document.body.classList.add('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, [show]);

  useEffect(() => {
    if (!show) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === '+' || e.key === '=') aplicarZoom(PASSO_BOTAO);
      else if (e.key === '-' || e.key === '_') aplicarZoom(1 / PASSO_BOTAO);
      else if (e.key === '0') resetar();
      else if (e.key === 'r') girar(90);
      else if (e.key === 'R') girar(-90);
    };

    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [show, onClose, aplicarZoom, resetar, girar]);

  // Listener manual: o React registra wheel como passivo, e sem
  // preventDefault a página atrás do overlay rolaria junto.
  useEffect(() => {
    const overlay = overlayRef.current;
    if (!show || !overlay) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      aplicarZoom(e.deltaY < 0 ? PASSO_RODA : 1 / PASSO_RODA, e.clientX, e.clientY);
    };

    overlay.addEventListener('wheel', handleWheel, { passive: false });
    return () => overlay.removeEventListener('wheel', handleWheel);
  }, [show, aplicarZoom]);

  const handlePointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;

    arraste.current = {
      x: e.clientX - offset.x,
      y: e.clientY - offset.y,
      downX: e.clientX,
      downY: e.clientY,
      moveu: false,
      // Precisa ser lido agora: com pointer capture, o target dos eventos
      // seguintes passa a ser o próprio overlay.
      noFundo: e.target === e.currentTarget,
    };
    setArrastando(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    const inicio = arraste.current;
    if (!inicio) return;

    if (
      Math.abs(e.clientX - inicio.downX) > 3 ||
      Math.abs(e.clientY - inicio.downY) > 3
    ) {
      inicio.moveu = true;
    }
    if (escala === ESCALA_MIN) return;

    setOffset(limitarOffset(e.clientX - inicio.x, e.clientY - inicio.y, escala));
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    const inicio = arraste.current;
    if (!inicio) return;

    arraste.current = null;
    setArrastando(false);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }

    // Clique no fundo (sem arraste) fecha; clique na imagem, não.
    if (inicio.noFundo && !inicio.moveu) onClose();
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    if (escala > ESCALA_MIN) resetar();
    else aplicarZoom(2, e.clientX, e.clientY);
  };

  if (!show) return null;

  // O portal fica dentro de um fragmento: `createPortal` isolado no return
  // esbarra na cópia duplicada de @types/react sob @types/react-dom.
  return (
    <>
      {ReactDOM.createPortal(
        <div
          ref={overlayRef}
          className={styles.overlay}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onDoubleClick={handleDoubleClick}
        >
          <div
            className={styles.barra}
            onPointerDown={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className={styles.botao}
              title="Diminuir zoom (-)"
              onClick={() => aplicarZoom(1 / PASSO_BOTAO)}
              disabled={escala <= ESCALA_MIN}
            >
              <Icon path={mdiMagnifyMinusOutline} size={1} />
            </button>

            <span className={styles.percentual}>
              {Math.round(escala * 100)}%
            </span>

            <button
              type="button"
              className={styles.botao}
              title="Aumentar zoom (+)"
              onClick={() => aplicarZoom(PASSO_BOTAO)}
              disabled={escala >= ESCALA_MAX}
            >
              <Icon path={mdiMagnifyPlusOutline} size={1} />
            </button>

            <button
              type="button"
              className={styles.botao}
              title="Ajustar à tela (0)"
              onClick={resetar}
              disabled={escala === ESCALA_MIN}
            >
              <Icon path={mdiFitToScreenOutline} size={1} />
            </button>

            <span className={styles.separador} />

            <button
              type="button"
              className={styles.botao}
              title="Girar à esquerda (Shift+R)"
              onClick={() => girar(-90)}
            >
              <Icon path={mdiRotateLeft} size={1} />
            </button>

            <button
              type="button"
              className={styles.botao}
              title="Girar à direita (R)"
              onClick={() => girar(90)}
            >
              <Icon path={mdiRotateRight} size={1} />
            </button>

            <span className={styles.separador} />

            <a
              className={styles.botao}
              href={src}
              download={nomeArquivo ?? ''}
              title="Baixar"
            >
              <Icon path={mdiDownload} size={1} />
            </a>

            <a
              className={styles.botao}
              href={src}
              target="_blank"
              rel="noreferrer"
              title="Abrir em nova aba"
            >
              <Icon path={mdiOpenInNew} size={1} />
            </a>

            <button
              type="button"
              className={styles.botao}
              title="Fechar (Esc)"
              onClick={onClose}
            >
              <Icon path={mdiClose} size={1} />
            </button>
          </div>

          <img
            ref={imgRef}
            className={[styles.img, 'rr-block'].join(' ')}
            src={src}
            alt={alt ?? 'imagem'}
            draggable={false}
            style={{
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${escala * fatorRotacao(deitada)}) rotate(${rotacao}deg)`,
              transition: arrastando ? 'none' : 'transform 0.15s ease-out',
              cursor:
                escala > ESCALA_MIN
                  ? arrastando
                    ? 'grabbing'
                    : 'grab'
                  : 'zoom-in',
            }}
          />
        </div>,
        document.body,
      )}
    </>
  );
};

export default VisualizadorImagem;
